import React from "react";
import { Network, Eye, Sparkles, Loader2 } from "lucide-react";

type KnowledgeGraphSectionProps = {
  title: string;
  subtitle: string;
  nodeCount: number;
  keywordCount: number;
  viewUrl: string | null;
  isBuilding?: boolean;
};

export function KnowledgeGraphSection({
  title,
  subtitle,
  nodeCount,
  keywordCount,
  viewUrl,
  isBuilding = false,
}: KnowledgeGraphSectionProps) {
  return (
    <article className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
      <div className="flex items-center gap-6 border-b-[0.8px] border-[#E2E8F0] bg-[#F8FAFC] px-5 py-4">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="inline-flex items-center gap-1">
            <Network className="h-5 w-5 text-[#0F172B]" />
            <h3 className="text-[20px] font-semibold leading-none text-[#0F172B]">
              {title}
            </h3>
          </div>
          <p className="text-[14px] font-normal leading-none text-[#45556C]">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-[14px] bg-white px-5 py-4">
        <div className="relative h-[321px] overflow-hidden rounded-xl border border-[#E2E8F0] bg-white p-2">
          <div className="absolute left-3 top-3 z-10 inline-flex items-center gap-2 rounded-full bg-[#F8FAFC] px-3 py-2 text-[28px] text-[#007AFF]">
            <Sparkles className="h-4 w-4" />
            <span className="text-[14px] font-semibold leading-none text-[#007AFF]">
              {isBuilding
                ? "Analyse en cours…"
                : nodeCount > 0
                ? `${keywordCount} Mots-clés identifiés`
                : "En attente des transcriptions"}
            </span>
          </div>
          {viewUrl && nodeCount > 0 && (
            <a
              href={viewUrl}
              target="_blank"
              rel="noreferrer"
              className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-[12px] border border-[#E2E8F0] bg-[#F8FAFC] text-[#45556C] transition-colors hover:bg-white"
              aria-label="Ouvrir le graphe en plein écran"
            >
              <Eye className="h-4 w-4" />
            </a>
          )}

          <div className="relative h-full w-full overflow-hidden rounded-[10px] bg-[#F8FAFC]">
            {viewUrl && nodeCount > 0 ? (
              <iframe
                key={nodeCount}
                src={viewUrl}
                title="Knowledge graph"
                className="h-full w-full border-0"
              />
            ) : isBuilding ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-4">
                <div className="relative h-24 w-40">
                  {[
                    "left-[10%] top-[60%]",
                    "left-[30%] top-[30%]",
                    "left-[50%] top-[55%]",
                    "left-[70%] top-[25%]",
                    "left-[85%] top-[60%]",
                    "left-[45%] top-[15%]",
                  ].map((pos, idx) => (
                    <span
                      key={idx}
                      className={`absolute ${pos} h-2.5 w-2.5 rounded-full bg-[#007AFF] animate-pulse`}
                      style={{ animationDelay: `${idx * 0.15}s` }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 text-[#45556C]">
                  <Loader2 className="h-4 w-4 animate-spin text-[#007AFF]" />
                  <span className="text-[13px] font-medium">Construction du graphe…</span>
                </div>
              </div>
            ) : (
              // Surtout pas de fausse constellation ici : afficher des pastilles
              // décoratives quand nodeCount vaut 0 laissait croire à un graphe
              // réel mais vide, alors que le graphe était simplement absent ou
              // encore en cours d'écriture côté serveur.
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center">
                <Network className="h-8 w-8 text-[#CBD5E1]" />
                <p className="text-[13px] font-medium text-[#45556C]">
                  Aucun graphe pour l'instant
                </p>
                <p className="text-[12px] leading-snug text-[#94A3B8]">
                  Importez un fichier audio : le graphe se construit à partir des
                  transcriptions.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
